import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form/form';
import { Input } from '@/components/ui/input';
import { AuthButton } from '@/features/auth/components/auth-button';
import { AuthForm } from '@/features/auth/components/auth-form';
import { AuthLayout } from '@/features/auth/components/auth-layout';
import { AuthLink } from '@/features/auth/components/auth-link';

const forgotPasswordFormSchema = z.object({
  email: z.email('Please enter a valid email address'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordFormSchema>;

function ForgotPasswordPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);

  const { auth } = Route.useRouteContext();
  const { resetPassword } = auth;

  const form = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordFormSchema),
    defaultValues: {
      email: '',
    },
  });

  const handleSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    form.clearErrors('root');

    try {
      const result = await resetPassword(data.email);

      if (result.error) {
        form.setError('root', {
          type: 'manual',
          message: result.error.message || 'Failed to send reset email',
        });
        return;
      }

      setIsSent(true);
    } catch {
      form.setError('root', {
        type: 'manual',
        message: 'An unexpected error occurred. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout>
      <AuthForm
        onSubmit={form.handleSubmit(handleSubmit)}
        title="Forgot your password?"
        description={
          isSent ? 'Reset instructions have been sent to your email.' : ''
        }
      >
        <Form {...form}>
          <div className="grid grid-rows-[auto_1fr] gap-4">
            {/* Error message area - only takes space when needed */}
            <div className="overflow-hidden">
              <div
                className={`transition-all duration-200 ${
                  form.formState.errors.root
                    ? 'max-h-20 opacity-100'
                    : 'max-h-0 opacity-0'
                }`}
              >
                <div className="rounded-md bg-red-50 p-4">
                  <div className="text-sm text-red-700">
                    {form.formState.errors.root?.message}
                  </div>
                </div>
              </div>
            </div>

            {/* Form fields area */}
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="name@example.com"
                        autoCapitalize="none"
                        autoCorrect="off"
                        className="h-12"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </Form>

        <AuthButton type="submit" disabled={isLoading || isSent}>
          {isLoading
            ? 'Sending...'
            : isSent
              ? 'Instructions Sent'
              : 'Send reset instructions'}
        </AuthButton>
      </AuthForm>
      <AuthLink to="/login" text="Have an account?" linkText="Sign in" />
    </AuthLayout>
  );
}

const forgotPasswordSearchSchema = z.object({
  redirectTo: z.string().default('/').catch('/'),
});

export const Route = createFileRoute('/forgot-password')({
  validateSearch: forgotPasswordSearchSchema,
  beforeLoad: ({ context, search }) => {
    if (context.auth.isAuthenticated) {
      throw redirect({ to: search.redirectTo });
    }
  },
  component: ForgotPasswordPage,
});
